import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiLinkedin, FiMail, FiMapPin, FiPhone, FiBriefcase, FiCpu, FiTarget, FiTrendingUp, FiCheckCircle } from 'react-icons/fi';
import api from '../services/api';
import Header from '../components/common/Header';
import './Landing.css';

function Landing() {
  const [data, setData] = useState({ profile: {}, items: [] });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await api.get('/profile/');
        setData(response.data || { profile: {}, items: [] });
      } catch (error) {
        console.error('Failed to load profile', error);
      }
    };
    loadProfile();
  }, []);

  const profile = data.profile || {};
  const items = Array.isArray(data.items) ? data.items : [];
  const strategic = items.filter((item) => item.section === 'strategic_leadership');
  const technical = items.filter((item) => item.section === 'technical_excellence');
  const delivery = items.filter((item) => item.section === 'delivery_excellence');
  const experience = items.find((item) => item.section === 'experience');
  const highlights = items.filter((item) => item.section === 'experience_highlights');
  const technicalExpertise = Array.isArray(profile.technicalExpertise) ? profile.technicalExpertise : [];
  const executiveImpactMetrics = Array.isArray(profile.executiveImpactMetrics) ? profile.executiveImpactMetrics : [];
  const keyAchievements = Array.isArray(profile.keyAchievements) ? profile.keyAchievements : [];

  return (
    <div className="app-shell premium-theme">
      <Header
        brand="HASEEB AHMED"
        links={[
          { label: 'Summary', href: '#summary' },
          { label: 'Competencies', href: '#competencies' },
          { label: 'Experience', href: '#experience' },
          { label: 'Contact', href: '#contact' }
        ]}
        action={<Link to="/dashboard" className="btn btn-primary">View Insights <FiArrowRight /></Link>}
      />

      <section className="hero">
        <div className="container hero-grid">
          <div>
            <div className="eyebrow"><FiTrendingUp /> Executive Leadership Profile</div>
            <h1>{profile.name || 'Haseeb Ahmed'}</h1>
            <p>
              <strong>{profile.headline}</strong><br />
              {profile.summary}
            </p>
            <div className="hero-actions">
              <a href="#contact" className="btn btn-primary">Connect Now <FiArrowRight /></a>
              <a href="#experience" className="btn btn-secondary">Explore Experience</a>
            </div>
            <div className="stats-row">
              <div className="stat-tile"><div className="stat-value">{profile.heroStat1}</div><div className="stat-label">Executive Experience</div></div>
              <div className="stat-tile"><div className="stat-value">{profile.heroStat2}</div><div className="stat-label">Team Leadership</div></div>
              <div className="stat-tile"><div className="stat-value">{profile.heroStat3}</div><div className="stat-label">Regional Reach</div></div>
              <div className="stat-tile"><div className="stat-value">{profile.heroStat4}</div><div className="stat-label">Delivery Reliability</div></div>
            </div>
          </div>

          <div className="profile-card premium-card">
            <div className="profile-avatar">HA</div>
            <h3>{profile.headline}</h3>
            <p>Product strategy, AI architecture, cross-functional execution, and enterprise-scale transformation leadership.</p>
            <div className="contact-list">
              <div className="contact-item"><FiMapPin /> <div><strong>Location</strong><br />{profile.location}</div></div>
              <div className="contact-item"><FiPhone /> <div><strong>Phone</strong><br />{profile.phone}</div></div>
              <div className="contact-item"><FiMail /> <div><strong>Email</strong><br />{profile.email}</div></div>
              <div className="contact-item"><FiLinkedin /> <div><strong>LinkedIn</strong><br />{profile.linkedin}</div></div>
            </div>
          </div>
        </div>
      </section>

      <section id="summary" className="section section-dark">
        <div className="container">
          <h2 className="section-title">Executive Summary</h2>
          <p className="section-subtitle">
            Visionary technology executive with 7+ years driving enterprise digital transformation through AI-powered solutions, agile methodologies, and strategic product innovation.
          </p>
          <div className="grid-3">
            <div className="card">
              <h3><FiBriefcase /> Strategic Leadership</h3>
              <div className="competency-list">
                {strategic.map((item) => (
                  <div className="list-item" key={item.id}><span className="list-icon"><FiTarget /></span><span>{item.title}</span></div>
                ))}
              </div>
            </div>
            <div className="card">
              <h3><FiCpu /> Technical Excellence</h3>
              <div className="competency-list">
                {technical.map((item) => (
                  <div className="list-item" key={item.id}><span className="list-icon"><FiCpu /></span><span>{item.title}</span></div>
                ))}
              </div>
            </div>
            <div className="card">
              <h3><FiCheckCircle /> Delivery Excellence</h3>
              <div className="competency-list">
                {delivery.map((item) => (
                  <div className="list-item" key={item.id}><span className="list-icon"><FiCheckCircle /></span><span>{item.title}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="competencies" className="section">
        <div className="container">
          <h2 className="section-title">Core Competencies</h2>
          <p className="section-subtitle">A balanced leadership profile combining strategy, architecture, delivery governance, and stakeholder management.</p>
          <div className="grid-3">
            <div className="card">
              <h4>AI & Solution Architecture</h4>
              <p>Leading AI transformation initiatives across generative AI, computer vision, full-stack architecture, and ML pipelines.</p>
            </div>
            <div className="card">
              <h4>Program & Product Execution</h4>
              <p>Managing budgets over $500K, sprint planning, cross-functional coordination, and roadmap alignment across multiple workstreams.</p>
            </div>
            <div className="card">
              <h4>Enterprise Delivery Leadership</h4>
              <p>Building high-performance teams, driving risk management, and sustaining operational excellence for enterprise-grade deployments.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">Technical Expertise</h2>
          <p className="section-subtitle">Hands-on technical depth across AI, cloud platforms, development ecosystems, and delivery operations.</p>
          <div className="grid-3">
            {technicalExpertise.map((group) => (
              <div className="card" key={group.title}>
                <h4>{group.title}</h4>
                <div className="competency-list">
                  {(Array.isArray(group.items) ? group.items : []).map((entry) => (
                    <div className="list-item" key={entry}>
                      <span className="list-icon"><FiCheckCircle /></span>
                      <span>{entry}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="experience" className="section">
        <div className="container">
          <h2 className="section-title">Professional Experience</h2>
          <p className="section-subtitle">A results-focused leadership journey centered on scalable AI-driven digital transformation.</p>
          <div className="card experience-card">
            <div className="experience-head">
              <div>
                <div className="experience-role">{experience?.title}</div>
                <div className="experience-company">{experience?.subtitle}</div>
              </div>
              <div className="experience-meta">{experience?.meta}</div>
            </div>
            <p>{experience?.description}</p>
            <div className="highlight-list">
              {highlights.map((item) => (
                <div className="list-item" key={item.id}>
                  <span className="list-icon"><FiArrowRight /></span>
                  <span>{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">Executive Impact Metrics</h2>
          <p className="section-subtitle">Quantified leadership outcomes across delivery, scale, and business performance.</p>
          <div className="stats-row">
            {executiveImpactMetrics.map((metric) => (
              <div className="stat-tile" key={metric.label}>
                <div className="stat-value">{metric.value}</div>
                <div className="stat-label">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Key Achievements</h2>
          <p className="section-subtitle">Selected leadership outcomes demonstrating transformation, scale, and execution excellence.</p>
          <div className="grid-3">
            {keyAchievements.map((achievement) => (
              <div className="card" key={achievement.title}>
                <h4>{achievement.title}</h4>
                <p>{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section section-dark">
        <div className="container">
          <h2 className="section-title">Contact Information</h2>
          <p className="section-subtitle">Open to strategic leadership, product innovation, AI transformation, and enterprise delivery opportunities.</p>
          <div className="grid-3">
            <div className="card"><h4>Location</h4><p>{profile.location}</p></div>
            <div className="card"><h4>Phone</h4><p>{profile.phone}</p></div>
            <div className="card"><h4>Email</h4><p>{profile.email}</p></div>
            <div className="card"><h4>LinkedIn</h4><p>{profile.linkedin}</p></div>
            <div className="card"><h4>Portfolio</h4><p>{profile.portfolio}</p></div>
            <div className="card"><h4>Professional Theme</h4><p>Premium executive one-page profile with dark luxury surfaces and refined presentation.</p></div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container footer-inner">
          <div>
            <strong>Haseeb Ahmed</strong><br />
            Executive Product & Project Leader | AI Solutions Architect
          </div>
          <div>© 2026 Haseeb Ahmed. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
